import { MihatoMark } from "@/common/components/MihatoMark";
import { ApiError } from "@/common/lib/api";
import { useCreateAccessRequest } from "@/features/auth/api/authApi";
import {
  type AccessRequestFormInput,
  type FieldErrors,
  validateAccessRequest,
} from "@/features/auth/schemas";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export function AccessRequestPage() {
  const [form, setForm] = useState<AccessRequestFormInput>({ email: "", phone: "" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const mutation = useCreateAccessRequest();

  function update<K extends keyof AccessRequestFormInput>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fieldErrors = validateAccessRequest(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    try {
      await mutation.mutateAsync({
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        toast.error("Muchos intentos, esperá un momento y volvé a probar.");
        return;
      }
      // Policy: siempre mostramos éxito salvo falla de red/rate-limit → privacy
      // para el create endpoint. Si hubo otro error (500/network) mostramos
      // toast neutral y dejamos el form visible.
      toast.error("No pudimos enviar tu solicitud. Revisá tu conexión e intentá de nuevo.");
    }
  }

  return (
    <>
      <title>Solicitar acceso · HATO</title>
      <meta name="robots" content="noindex, nofollow" />

      <div
        className="landing-texture pointer-events-none fixed inset-0 opacity-50"
        aria-hidden="true"
      />

      <main className="relative min-h-screen bg-brand-dark text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[90vh]"
          style={{
            background:
              "radial-gradient(circle at 50% 10%, rgba(0, 229, 153, 0.08) 0%, transparent 60%)",
          }}
        />

        <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
          <Link to="/" className="mb-10 flex items-center gap-3 self-start">
            <MihatoMark className="h-7 w-7 text-white" />
            <span className="font-medium text-lg tracking-tight">HATO</span>
          </Link>

          {submitted ? (
            <output className="flex flex-col gap-4 rounded-2xl border border-primary-500/30 bg-primary-500/[0.06] p-6">
              <h1 className="font-medium text-2xl text-white tracking-tight md:text-3xl">
                ✓ Recibimos tu solicitud
              </h1>
              <p className="text-neutral-300 text-sm leading-relaxed">
                Te vamos a contactar al email que nos diste. Si fuiste aprobado, recibirás un link
                para ingresar.
              </p>
              <Link
                to="/"
                className="mt-2 flex h-12 items-center justify-center rounded-full border border-white/[0.1] px-6 font-medium text-sm text-white transition-colors hover:bg-white/[0.05]"
              >
                Volver al inicio
              </Link>
            </output>
          ) : (
            <>
              <div className="mb-8 flex flex-col gap-2">
                <h1 className="font-medium text-3xl text-white tracking-tight md:text-4xl">
                  Solicitar acceso
                </h1>
                <p className="text-neutral-400 text-sm">
                  Dejanos tu email y teléfono. Te contactamos para habilitar tu acceso.
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                <Field label="Correo" htmlFor="email" error={errors.email}>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="tu@ejemplo.com"
                    aria-invalid={Boolean(errors.email)}
                    className={inputClass}
                    disabled={mutation.isPending}
                  />
                </Field>

                <Field
                  label="Teléfono"
                  htmlFor="phone"
                  error={errors.phone}
                  hint="Formato internacional con código de país. Ejemplo: +506 8888 7777."
                >
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    autoComplete="tel"
                    inputMode="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+506 8888 7777"
                    aria-invalid={Boolean(errors.phone)}
                    className={inputClass}
                    disabled={mutation.isPending}
                  />
                </Field>

                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="mt-2 flex h-12 items-center justify-center rounded-full bg-primary-500 px-6 font-medium text-brand-dark text-sm transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {mutation.isPending ? "Enviando…" : "Solicitar acceso"}
                </button>
              </form>

              <p className="mt-6 text-center text-neutral-500 text-sm">
                ¿Ya tenés acceso?{" "}
                <Link to="/login" className="text-primary-500 hover:text-primary-400">
                  Ingresá acá
                </Link>
              </p>
            </>
          )}
        </div>
      </main>
    </>
  );
}

const inputClass =
  "h-12 w-full rounded-xl border border-white/[0.08] bg-brand-panel px-4 text-base text-white placeholder:text-neutral-500 focus:border-primary-500/60 focus:outline-none focus:ring-1 focus:ring-primary-500/40 disabled:opacity-60";

function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="font-medium text-neutral-300 text-sm">
        {label}
      </label>
      {children}
      {error ? (
        <p role="alert" className="text-rose-400 text-xs">
          {error}
        </p>
      ) : hint ? (
        <p className="text-neutral-500 text-xs">{hint}</p>
      ) : null}
    </div>
  );
}
